import {
  NgModule,
  CUSTOM_ELEMENTS_SCHEMA,
  NO_ERRORS_SCHEMA,
  LOCALE_ID,
  TransferState,
  inject,
} from '@angular/core';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { BrowserModule, provideClientHydration, withHttpTransferCacheOptions, withNoHttpTransferCache } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS, withFetch, provideHttpClient } from '@angular/common/http';
import { provideEventTracker, provideTrackingHttpInterceptor } from '@duocvuong/event-tracker/angular';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { ToastrModule } from 'ngx-toastr';

import { SharedModule } from './shared/shared.module';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { LayoutComponent } from './layout/layout.component';
import { CoreModule } from './core/core.module';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { AccountModule } from './components/account/account.module';
import localeVi from '@angular/common/locales/vi';
import { registerLocaleData } from '@angular/common';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { environment } from '@/environments/environment';
import { CurrencySymbolPipe } from './shared/pipe/currency-symbol.pipe';
import { IS_AUTHENTICATED } from './shared/services/auth.service';
import { UrlSerializer } from '@angular/router';
import { CleanUrlSerializer } from './shared/clean-url-serializer';
import { NgxGoogleAnalyticsModule, provideGoogleAnalytics } from '@hakimio/ngx-google-analytics';
import { register } from 'swiper/element/bundle';

registerLocaleData(localeVi, 'vi');
register();

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}
@NgModule({
  declarations: [AppComponent, LayoutComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ToastrModule.forRoot({
      positionClass: 'toast-top-center',
      preventDuplicates: true
    }),
    SharedModule,
    CoreModule,
    AccountModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    NgxGoogleAnalyticsModule
  ],
  providers: [
    CurrencySymbolPipe,
    {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
    // Ghi event hành vi: phiên, màn hình theo router, header truy vết gắn vào API
    provideEventTracker(() => {
      const cookies = inject(SsrCookieService);
      // configName chỉ có ở môi trường dùng chính sách từ ConfigurationV2, nên khai báo kiểu để các file môi trường khác vẫn biên dịch được
      const t: { enabled?: boolean; endpoint?: string; writeKey?: string; configName?: string } = environment.eventTracking;
      // SDK ném lỗi khi thiếu endpoint/writeKey dù enabled=false, và lỗi đó làm app không khởi động được.
      // Môi trường chưa cấu hình (writeKey rỗng) thì tắt hẳn và đưa giá trị giữ chỗ.
      const on = !!(t?.enabled && t.endpoint && t.writeKey);
      return {
        endpoint: t?.endpoint || 'https://tracking-disabled.invalid/e',
        writeKey: t?.writeKey || 'disabled',
        platform: 'web',
        apiOrigins: [environment.baseURL],
        getAccessToken: () => cookies.get('token'),
        enabled: on,
        // Có configName: chờ AppComponent lấy config từ ConfigurationV2 rồi đưa vào SDK (tracker.applyPolicy).
        // Chưa có config, lỗi, hoặc quá 5 giây thì TẮT. Không có configName thì ghi mọi event.
        policy: on && t.configName ? ('wait' as const) : undefined,
        strict: false,
      };
    }, {
      // Link chiến dịch voucher (utm_campaign = mã vc + 5 ký tự) bấm từ popup hay banner trong ứng dụng: SDK chỉ đọc utm của URL lúc mở phiên.
      // Mỗi lần chuyển trang có mã chiến dịch thì SDK gắn utm_* vào session_started (mở phiên mới nếu đang ở phiên khác chiến dịch). Banner có utm_campaign khác giữ nguyên.
      trackCampaignRoutes: (c) => /^vc[a-z0-9]{5}$/.test(c.utm_campaign ?? ''),
    }),
    provideTrackingHttpInterceptor(),
    { provide: LOCALE_ID, useValue: "vi" },
    // Bỏ query param không có giá trị khỏi URL (?searchTerm=, ?category)
    { provide: UrlSerializer, useClass: CleanUrlSerializer },
    // provideServerRendering(),
    provideClientHydration(
      withHttpTransferCacheOptions({
        includePostRequests: true,
        // Chỉ dùng transfer cache cho khách chưa đăng nhập: response riêng của user (giá, giỏ hàng...)
        // không được nhúng vào HTML. IS_AUTHENTICATED được set trên server trong AppRoutingModule.
        filter: () => !inject(TransferState).get(IS_AUTHENTICATED, false),
      })
    ),
    provideHttpClient(withFetch()),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideMessaging(() => getMessaging()),
    provideGoogleAnalytics(environment.ga4TagId)
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule { }
